import { Alert, AlertDescription, AlertIcon, Button, Checkbox, Flex, Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { SpacingContainer } from '@resistapp/client/components/containers/spacing-container';
import { InputField } from '@resistapp/client/components/forms/form-inputs/input-field';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { InfoTooltipIcon } from '@resistapp/client/components/icons/info-tooltip-icon';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { PostOrganisationRequest, zPostOrganisationRequest } from '@resistapp/common/api-types';
import {
  Feature,
  LabFeature,
  allLabFeatures,
  defaultCustomerLabFeatures,
  resistomapOrganisationId,
} from '@resistapp/common/features';
import {
  MetricMode,
  Organisation,
  OrganisationSummary,
  OrganisationType,
  OrganisationUserRole,
} from '@resistapp/common/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParamsContext } from '@resistapp/client/contexts/search-params-context';
import { useForm, useWatch } from 'react-hook-form';
import useLocalStorageState from 'use-local-storage-state';
import { ControlledInput } from '../../components/forms/form-inputs/controlled-input';
import { ControlledSelect } from '../../components/forms/form-inputs/controlled-select';
import { usePatchOrganisation, useProjects, useOrganisations } from '../../hooks/api';
import { useUser } from '../../contexts/use-user-context';
import { isResistomapAdminUser } from '../../utils/access';

type CreateOrganisationFormValues = Omit<PostOrganisationRequest, 'parentId'> & { parentId: number | null };

export function CreateOrganisationView() {
  const { searchParams } = useSearchParamsContext();
  const parentIdFromQuery = searchParams.get('parentId');
  const parsedParentId = parentIdFromQuery ? Number(parentIdFromQuery) : null;
  const childIdFromQuery = searchParams.get('childId');
  const parsedChildId = childIdFromQuery ? Number(childIdFromQuery) : null;
  const childOrganisationId =
    typeof parsedChildId === 'number' && Number.isFinite(parsedChildId) ? parsedChildId : null;
  const [createdParentOrganisation, setCreatedParentOrganisation] = useState<Organisation | null>(null);

  const defaultValues: CreateOrganisationFormValues = useMemo(
    () => ({
      name: '',
      externalName: '',
      type: OrganisationType.CUSTOMER,
      parentId: typeof parsedParentId === 'number' && Number.isFinite(parsedParentId) ? parsedParentId : null,
      projectAccesses: [],
      samplingPointAccesses: [],
      sampleAccesses: [],
      features: [],
      labFeatures: [...defaultCustomerLabFeatures],
      isDemo: false,
      defaultMetric: MetricMode.ARGI,
    }),
    [parsedParentId],
  );
  const createOrganisation = usePatchOrganisation();
  const linkChildOrganisation = usePatchOrganisation();
  const { data: projects, error: projectsError } = useProjects();
  const { data: organisations, error: organisationsError } = useOrganisations();
  const { user } = useUser();
  const isResistomapAdmin = isResistomapAdminUser(user);
  const [, setOrganisationCreated] = useLocalStorageState(LocalStorageId.organisationCreated, {
    defaultValue: false,
  });
  const navigate = useNavigateWithQuery();
  const {
    handleSubmit,
    register,
    reset,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateOrganisationFormValues>({
    resolver: zodResolver(zPostOrganisationRequest),
    defaultValues,
  });

  const typeValue = useWatch({ control, name: 'type', defaultValue: OrganisationType.CUSTOMER });
  const watchedParentId = useWatch({ control, name: 'parentId' });
  const parentIdValue = typeof watchedParentId === 'number' ? watchedParentId : null;
  const previousTypeRef = useRef<OrganisationType | undefined>(undefined);

  useEffect(() => {
    const previous = previousTypeRef.current;
    if (typeValue === previous) {
      return;
    }
    previousTypeRef.current = typeValue;

    if (typeValue === OrganisationType.CUSTOMER) {
      setValue('labFeatures', [...defaultCustomerLabFeatures], { shouldDirty: false });
      setValue('defaultMetric', MetricMode.ARGI, { shouldDirty: false });
    } else if (typeValue === OrganisationType.RESISTOMAP) {
      setValue('labFeatures', [...allLabFeatures], { shouldDirty: false });
      setValue('defaultMetric', MetricMode.ARGI, { shouldDirty: false });
    } else {
      setValue('labFeatures', [...allLabFeatures], { shouldDirty: false });
      setValue('defaultMetric', MetricMode.ARGI, { shouldDirty: false });
    }
  }, [setValue, typeValue]);

  useEffect(() => {
    if (childOrganisationId == null) {
      setCreatedParentOrganisation(null);
    }
  }, [childOrganisationId]);

  const adminOrgMemberships = useMemo(
    () => (user?.organisations ?? []).filter(org => org.membershipRole === OrganisationUserRole.ADMIN),
    [user],
  );

  const childrenByParentId = useMemo(() => {
    const map = new Map<number, OrganisationSummary[]>();
    (organisations ?? []).forEach(org => {
      if (org.parentId == null) {
        return;
      }
      const siblings = map.get(org.parentId) ?? [];
      siblings.push(org);
      map.set(org.parentId, siblings);
    });
    return map;
  }, [organisations]);

  const descendantCustomerIds = useMemo(() => {
    if (adminOrgMemberships.length === 0) {
      return new Set<number>();
    }
    const result = new Set<number>();
    const visit = (rootId: number) => {
      const queue = [...(childrenByParentId.get(rootId) ?? [])];
      while (queue.length > 0) {
        const current = queue.shift() as OrganisationSummary;
        if (current.type === OrganisationType.CUSTOMER) {
          result.add(current.id);
        }
        const children = childrenByParentId.get(current.id);
        if (children && children.length > 0) {
          queue.push(...children);
        }
      }
    };
    adminOrgMemberships.forEach(org => {
      visit(org.id);
    });
    return result;
  }, [adminOrgMemberships, childrenByParentId]);

  const organisationById = useMemo(() => {
    const map = new Map<number, OrganisationSummary>();
    (organisations ?? []).forEach(org => map.set(org.id, org));
    return map;
  }, [organisations]);

  const allowedParentIds = useMemo(() => {
    const allowed = new Set<number>();
    if (!organisations) {
      return allowed;
    }
    const addIfEligible = (org: Pick<Organisation, 'id' | 'type'> | undefined) => {
      if (!org || org.type === OrganisationType.LAB) {
        return;
      }
      allowed.add(org.id);
    };

    adminOrgMemberships.forEach(org => {
      addIfEligible(organisationById.get(org.id) ?? org);
    });

    descendantCustomerIds.forEach(id => {
      addIfEligible(organisationById.get(id));
    });

    if (isResistomapAdmin) {
      organisations.filter(org => org.type === OrganisationType.PROVIDER).forEach(addIfEligible);
    }

    return allowed;
  }, [adminOrgMemberships, descendantCustomerIds, organisationById, organisations, isResistomapAdmin]);

  const parentOptions = useMemo(() => {
    if (!organisations || allowedParentIds.size === 0) {
      return [] as OrganisationSummary[];
    }
    return organisations
      .filter(organisation => allowedParentIds.has(organisation.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allowedParentIds, organisations]);

  const resolvedParentId = useMemo(() => {
    if (parentOptions.length === 0) {
      return null;
    }
    if (parsedParentId != null && allowedParentIds.has(parsedParentId)) {
      return parsedParentId;
    }
    if (isResistomapAdmin) {
      const resistomapOption = parentOptions.find(org => org.type === OrganisationType.RESISTOMAP);
      if (resistomapOption) {
        return resistomapOption.id;
      }
    }
    const providerAdminOrg = adminOrgMemberships.find(org => org.type === OrganisationType.PROVIDER);
    if (providerAdminOrg && allowedParentIds.has(providerAdminOrg.id)) {
      return providerAdminOrg.id;
    }
    const fallbackAdminOrg = adminOrgMemberships.find(org => allowedParentIds.has(org.id));
    if (fallbackAdminOrg) {
      return fallbackAdminOrg.id;
    }
    return parentOptions[0]?.id ?? null;
  }, [adminOrgMemberships, allowedParentIds, isResistomapAdmin, parentOptions, parsedParentId]);

  useEffect(() => {
    if (resolvedParentId == null) {
      if (parentIdValue != null && !allowedParentIds.has(parentIdValue)) {
        setValue('parentId', null, { shouldDirty: false });
      }
      return;
    }
    if (parentIdValue === resolvedParentId) {
      return;
    }
    setValue('parentId', resolvedParentId, { shouldDirty: false });
  }, [allowedParentIds, parentIdValue, resolvedParentId, setValue]);

  const noParentOptions = (organisations?.length ?? 0) > 0 && parentOptions.length === 0;

  const availableLabFeatures: LabFeature[] = useMemo(
    () => (typeValue === OrganisationType.CUSTOMER ? [...defaultCustomerLabFeatures] : [...allLabFeatures]),
    [typeValue],
  );
  const availableFeatures = useMemo(() => Object.values(Feature).filter(feature => feature !== Feature.ADMIN), []);

  if (projectsError) {
    throw projectsError;
  }

  if (organisationsError) {
    throw organisationsError;
  }

  const onSubmit = async (values: CreateOrganisationFormValues) => {
    createOrganisation.reset();
    linkChildOrganisation.reset();
    try {
      if (values.parentId == null) {
        throw new Error('Parent organisation is required');
      }
      const enforcedType = isResistomapAdmin ? values.type : OrganisationType.CUSTOMER;
      const payload: PostOrganisationRequest = {
        ...values,
        type: enforcedType,
        parentId: values.parentId,
        features: Array.from(values.features),
        labFeatures:
          enforcedType === OrganisationType.CUSTOMER
            ? Array.from(defaultCustomerLabFeatures)
            : Array.from(new Set(values.labFeatures.length > 0 ? values.labFeatures : availableLabFeatures)),
        defaultMetric:
          enforcedType === OrganisationType.CUSTOMER ? values.defaultMetric ?? MetricMode.ARGI : MetricMode.ARGI,
      };
      const shouldReuseParent = childOrganisationId != null && createdParentOrganisation != null;
      const createdOrganisation = shouldReuseParent
        ? createdParentOrganisation
        : await createOrganisation.mutateAsync(payload);

      if (childOrganisationId != null && !shouldReuseParent) {
        setCreatedParentOrganisation(createdOrganisation);
      }

      if (childOrganisationId != null) {
        await linkChildOrganisation.mutateAsync({ id: childOrganisationId, parentId: createdOrganisation.id });
      }

      setOrganisationCreated(true);
      navigate('/admin', false);
      reset(defaultValues);
      setCreatedParentOrganisation(null);
    } catch (_error) {
      console.error(_error);
    }
  };

  const onValidationFailed = (_errors: any) => {
    console.warn('Form validation failed', _errors);
  };

  return (
    <Flex direction="column" alignItems="center">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <SpacingContainer as="form" onSubmit={handleSubmit(onSubmit, onValidationFailed)}>
        <RSecondTitle>Create an organisation</RSecondTitle>

        <Stack spacing={6}>
          <InputField
            key="name"
            field={{
              name: 'name',
              label: 'Organisation name*',
              type: 'text',
            }}
            errors={errors.name}
            register={register}
          />
          <InputField
            key="externalName"
            field={{
              name: 'externalName',
              label: 'External name (optional)',
              type: 'text',
            }}
            errors={errors.externalName}
            register={register}
          />
          {isResistomapAdmin ? (
            <ControlledSelect
              control={control}
              name="type"
              options={[OrganisationType.CUSTOMER, OrganisationType.PROVIDER, OrganisationType.LAB]}
              placeholder="Select organisation type"
              closeMenuOnSelect={true}
            />
          ) : null}
          <ControlledSelect
            control={control}
            name="parentId"
            options={parentOptions}
            label="Parent organisation"
            placeholder="Select parent organisation"
            labeller={(option: OrganisationSummary) =>
              option.id === resistomapOrganisationId || option.type === OrganisationType.RESISTOMAP
                ? 'None'
                : `${option.name} (${option.type})`
            }
            closeMenuOnSelect={true}
            isClearable={false}
            isRequired
            disabled={parentOptions.length === 0}
          />
          {noParentOptions ? (
            <Alert status="warning">
              <AlertIcon />
              <AlertDescription>
                You need admin access to a provider or customer organisation before creating new organisations. Please
                contact Resistomap support to be added as an admin.
              </AlertDescription>
            </Alert>
          ) : null}
          <ControlledSelect
            control={control}
            name="features"
            options={availableFeatures}
            label="Features"
            closeMenuOnSelect={true}
            placeholder="Select features"
            isMulti
            disabled={typeValue !== OrganisationType.CUSTOMER}
          />
          <ControlledSelect
            control={control}
            name="projectAccesses"
            options={projects || []}
            label="Project access"
            placeholder="Select projects"
            labeller={option => option.name}
            isMulti
          />
          <ControlledSelect
            control={control}
            name="defaultMetric"
            options={Object.values(MetricMode)}
            label="Default metric"
            closeMenuOnSelect={true}
            placeholder="Select default metric"
            disabled={typeValue !== OrganisationType.CUSTOMER}
          />
          <ControlledSelect
            control={control}
            name="labFeatures"
            options={availableLabFeatures}
            isMulti
            closeMenuOnSelect={false}
            label="Lab features"
            placeholder="Select lab features"
            disabled={!isResistomapAdmin || typeValue === OrganisationType.CUSTOMER}
          />

          <ControlledInput control={control} name="isDemo" label={null}>
            <Checkbox size="lg">
              Demo organisation{' '}
              <InfoTooltipIcon>
                Check if the organisation is created for demo purposes, and not for a real customer. Only demo
                organisations are allowed to have users that are not part of the organisation legal entity in the real
                world (eg. partners or collaborators).
              </InfoTooltipIcon>
            </Checkbox>
          </ControlledInput>
          {createOrganisation.error && (
            <Alert status="error" style={{ margin: '10px 0' }}>
              <AlertIcon />
              <AlertDescription>{createOrganisation.error.message}</AlertDescription>
            </Alert>
          )}
          {childOrganisationId != null && linkChildOrganisation.error && (
            <Alert status="error" style={{ margin: '10px 0' }}>
              <AlertIcon />
              <AlertDescription>
                Failed to link the new parent to the existing organisation.
                {createdParentOrganisation ? (
                  <>
                    {' '}
                    The parent organisation <strong>{createdParentOrganisation.name}</strong> has already been created,
                    so re-submitting will only retry the link without creating another organisation.
                  </>
                ) : null}{' '}
                Please try again or update the hierarchy manually. Error: {linkChildOrganisation.error.message}
              </AlertDescription>
            </Alert>
          )}
          <Button
            type="submit"
            isLoading={isSubmitting}
            isDisabled={Object.keys(errors).length > 0 || parentOptions.length === 0}
          >
            Create new
          </Button>
        </Stack>
      </SpacingContainer>
    </Flex>
  );
}
